/**
 * Раскладка Блоков на экране (тикет T18b, спецификация § 4).
 *
 * Расстановку считает ядро клиента, а этот файл переводит её в сетку и обратно: движение
 * руки — в клетки, нажатие клавиши — в тот же самый сдвиг на клетку. Оба пути ведут в одни
 * и те же `move` и `resize`, поэтому мышь и клавиатура не могут разойтись в поведении.
 *
 * Пока Блок держат, на сервер не уходит ничего. Иначе одно перетаскивание через пол-экрана
 * превратилось бы в полсотни запросов, а человек с медленной связью увидел бы, как Блок
 * дёргается вслед за ответами. Сервер узнаёт итог — один раз, когда Блок отпустили.
 *
 * Сетка не зеркалится при письме справа налево (G12): справа налево идёт текст внутри
 * Блока, а места Блоков задал Администратор. Поэтому у сетки своё направление, и оно
 * всегда одно.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent, ReactNode } from 'react';
import { GRID_COLUMNS } from '@core/layout/grid.js';
import type { Position } from '@core/layout/grid.js';
import type { Block, BlockKind, LayoutEditor, EditorState } from '@core/layout/editor.js';
import { layoutDirectionOf } from '@core/settings/appearance.js';
import { cellsMoved, movedTo, resizedTo, samePosition } from './geometry.js';
import { BlockCard } from './BlockCard.js';
import { READY_BLOCK_KINDS } from '../blocks/BlockBody.js';
import { Button, Notice } from '../ui/controls.js';
import { useT } from '../state/settings.js';

/** Высота клетки сетки — в размерах текста, чтобы крупный шрифт растил и Блоки. */
const ROW_HEIGHT_REM = 3.5;

/** Кого держим и как. */
interface Grab {
  readonly id: string;
  readonly mode: 'move' | 'resize';
  /** Откуда начали: рукой — точка на экране, с клавиатуры — её нет. */
  readonly from: { readonly x: number; readonly y: number } | null;
  readonly startPosition: Position;
}

const remInPixels = (): number => {
  if (typeof window === 'undefined') return 16;
  const size = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(size) && size > 0 ? size : 16;
};

export function LayoutBoard({
  editor,
  canArrange,
  canPersonalize = false,
  renderBlock,
  where,
}: {
  readonly editor: LayoutEditor;
  /** Собирать Раскладку может не всякий: Гость и обычный Сотрудник только смотрят. */
  readonly canArrange: boolean;
  /**
   * Настроить расстановку под себя может каждый, кому виден Канал, кроме Гостя. Общий
   * состав Блоков при этом не меняется — двигаются только места, и только на своём экране.
   */
  readonly canPersonalize?: boolean;
  readonly renderBlock: (block: Block) => ReactNode;
  /**
   * Название того, чей это экран, — его показывают перед выбором Типа. Человек, у которого
   * на виду несколько Каналов, иначе не знает, куда именно ляжет Блок, и ищет добавленное
   * не там (правка 2026-08-27).
   */
  readonly where?: string;
}): ReactNode {
  const t = useT();
  const gridRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<EditorState>(() => editor.state());
  const [arranging, setArranging] = useState(false);
  const [grab, setGrab] = useState<Grab | null>(null);
  /** Куда Блок встанет, если отпустить прямо сейчас. Показывается вместо настоящего места. */
  const [ghost, setGhost] = useState<Position | null>(null);
  const [askRemove, setAskRemove] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  /** Только что добавленный Блок: к нему уводят экран и его обводят, пока человек не увёл
   *  взгляд. Без этого Блок встаёт ниже сгиба, и добавивший решает, что не добавилось. */
  const [fresh, setFresh] = useState<string | null>(null);
  /** Что сказать голосом: движение Блока иначе не видно тому, кто не смотрит на экран. */
  const [spoken, setSpoken] = useState('');

  /**
   * Сказать вслух. Диктор молчит, если текст в живой области не изменился, — а фразы тут
   * повторяются слово в слово: два одинаковых Блока подряд, отпускание за отпусканием.
   * Поэтому повтор дописывает неразрывный пробел: ни глазу, ни голосу его нет, а строка
   * для браузера становится другой, и объявление звучит.
   */
  const say = useCallback((words: string): void => {
    setSpoken((was) => (was === words ? `${words} ` : words));
  }, []);

  useEffect(() => {
    setState(editor.state());
    const stop = editor.subscribe(setState);
    void editor.load();
    return stop;
  }, [editor]);

  /**
   * Узкий экран. Двенадцать колонок на телефоне нечитаемы: Блок в половину ширины — это
   * три сантиметра, а поле для Сообщения ужимается до двух букв. Поэтому на телефоне
   * Раскладка разворачивается в столбик — сверху вниз, в том же порядке, в каком Блоки
   * стоят на большом экране. Собирать Раскладку здесь нечем: сетки перед глазами нет,
   * и перетаскивание двигало бы Блок по местам, которых не видно.
   */
  const [stacked, setStacked] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 47.99rem)').matches,
  );
  useEffect(() => {
    const watch = window.matchMedia('(max-width: 47.99rem)');
    // Начальное значение уже взято при первом показе — здесь только следим за поворотом
    // телефона и изменением окна.
    const react = (): void => setStacked(watch.matches);
    watch.addEventListener('change', react);
    return () => watch.removeEventListener('change', react);
  }, []);

  const ordered = useMemo(
    () =>
      stacked
        ? [...state.blocks].sort(
            (a, b) => a.position.row - b.position.row || a.position.col - b.position.col,
          )
        : state.blocks,
    [state.blocks, stacked],
  );
  /**
   * Скрытые Блоки на экране не рисуются, но и не пропадают: они перечислены отдельной
   * строкой над Раскладкой, и вернуть любой из них — одно нажатие. Убери мы их совсем —
   * человек, скрывший Переписку, остался бы без способа её найти.
   */
  const hidden = useMemo(() => ordered.filter((one) => one.view.hidden), [ordered]);
  const blocks = useMemo(() => ordered.filter((one) => !one.view.hidden), [ordered]);
  const kindTitle = useCallback(
    (block: Block): string => state.kinds.find((one) => one.id === block.kind)?.title ?? block.kind,
    [state.kinds],
  );

  const minimumOf = useCallback(
    (block: Block): { width: number; height: number } => {
      const kind = state.kinds.find((one) => one.id === block.kind);
      return kind?.minimumSize ? { ...kind.minimumSize } : { width: 1, height: 1 };
    },
    [state.kinds],
  );

  /** Размер клетки прямо сейчас: ширина зависит от экрана, высота — от размера текста. */
  const cellSize = useCallback(() => {
    const box = gridRef.current?.getBoundingClientRect();
    return {
      width: box ? box.width / GRID_COLUMNS : 0,
      height: ROW_HEIGHT_REM * remInPixels(),
    };
  }, []);

  /**
   * Отпустить Блок и сказать серверу итог — один раз за всё перетаскивание.
   *
   * Отправляем по тому, что на деле изменилось, а не по тому, как Блок взяли. Рукой это
   * одно и то же: за заголовок Блок только двигают, за уголок только растягивают. А с
   * клавиатуры Блок берут один раз и делают им и то и другое — стрелками двигают, теми же
   * стрелками с Shift растягивают. Смотри мы на способ захвата, растянутый с клавиатуры
   * Блок вернулся бы к прежнему размеру, и человек решил бы, что Shift не работает.
   */
  const release = useCallback(
    async (final: Position | null): Promise<void> => {
      const held = grab;
      setGrab(null);
      setGhost(null);
      if (!held || !final) return;
      if (samePosition(final, held.startPosition)) return;

      const start = held.startPosition;
      const shifted = final.col !== start.col || final.row !== start.row;
      const grown = final.width !== start.width || final.height !== start.height;

      // По очереди, а не разом: ядро отказывает во второй правке, пока не закончилась
      // первая, — и вторая вернулась бы отказом «идёт сохранение» вместо дела.
      if (shifted) await editor.move(held.id, { col: final.col, row: final.row });
      if (grown) await editor.resize(held.id, { width: final.width, height: final.height });
      say(t('layout.released'));
    },
    [editor, grab, say, t],
  );

  // Рука. Слушаем окно, а не сам Блок: палец или курсор запросто уходит за края Блока, и
  // обработчик на Блоке потерял бы перетаскивание ровно в тот момент, когда оно началось.
  useEffect(() => {
    if (!grab?.from) return;
    const start = grab.from;

    const onMove = (event: globalThis.PointerEvent): void => {
      const by = cellsMoved({ x: event.clientX - start.x, y: event.clientY - start.y }, cellSize());
      const block = blocks.find((one) => one.id === grab.id);
      if (!block) return;
      setGhost(
        grab.mode === 'move'
          ? movedTo(grab.startPosition, by)
          : resizedTo(grab.startPosition, by, minimumOf(block)),
      );
    };

    const onUp = (): void => {
      // Читаем последнее показанное место через состояние-функцию: обработчик окна помнит
      // `ghost` таким, каким тот был при подписке, а нам нужен сегодняшний.
      setGhost((last) => {
        void release(last);
        return last;
      });
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [grab, blocks, cellSize, minimumOf, release]);

  const startPointer = (block: Block, mode: 'move' | 'resize') => (event: PointerEvent<HTMLElement>): void => {
    if (!arranging || state.saving) return;
    // Закреплённый не берётся вовсе, а не «берётся и возвращается на место»: человек иначе
    // тащил бы Блок через полэкрана и узнавал об отказе только отпустив руку.
    if (block.view.pinned) {
      say(t('block.pinnedSpoken'));
      return;
    }
    event.preventDefault();
    setGrab({
      id: block.id,
      mode,
      from: { x: event.clientX, y: event.clientY },
      startPosition: block.position,
    });
    setGhost(block.position);
  };

  const ARROWS: Readonly<Record<string, { cols: number; rows: number }>> = {
    ArrowLeft: { cols: -1, rows: 0 },
    ArrowRight: { cols: 1, rows: 0 },
    ArrowUp: { cols: 0, rows: -1 },
    ArrowDown: { cols: 0, rows: 1 },
  };

  /**
   * Клавиатура. Блок сперва «берут», и только потом двигают: иначе стрелки внутри
   * Раскладки перестали бы прокручивать экран, а человек, просто идущий по Блокам,
   * незаметно для себя переставил бы фирме половину рабочего места.
   */
  const onKeyDown = (block: Block) => (event: KeyboardEvent<HTMLElement>): void => {
    if (!arranging) return;
    const held = grab?.id === block.id ? grab : null;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (held) void release(ghost);
      else if (block.view.pinned) say(t('block.pinnedSpoken'));
      else {
        // Держали до этого другой Блок — он возвращается на своё место, как по Escape:
        // одновременно взятых Блоков не бывает, а показанное место первого стирается
        // вместе с захватом. Тихо сохранить его правку было бы хуже — человек двигал бы
        // Блок, на который уже не смотрит.
        setGrab({ id: block.id, mode: 'move', from: null, startPosition: block.position });
        setGhost(block.position);
        say(t('layout.grabbed'));
      }
      return;
    }

    if (!held) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      // Возврат на место, а не сохранение: Esc человек нажимает, когда передумал.
      setGrab(null);
      setGhost(null);
      say(t('layout.released'));
      return;
    }

    const step = ARROWS[event.key];
    if (!step) return;
    event.preventDefault();

    const from = ghost ?? block.position;
    // Что делают стрелки, решает то, зачем Блок взяли. Взяли на перенос — двигают, а Shift
    // меняет размер; взяли на размер (пункт меню «Изменить размер») — наоборот. Иначе
    // человек, выбравший в меню размер, стрелками двигал бы Блок и решил бы, что пункт врёт.
    const resizing = held.mode === 'resize' ? !event.shiftKey : event.shiftKey;
    const next = resizing
      ? resizedTo(from, step, minimumOf(block))
      : movedTo(from, step);
    setGhost(next);
    say(
      t('layout.spoken', {
        col: String(next.col + 1),
        row: String(next.row + 1),
        width: String(next.width),
        height: String(next.height),
      }),
    );
  };

  const shown = (block: Block): Position =>
    grab?.id === block.id && ghost ? ghost : block.position;

  /**
   * Два перекладывания — общее и личное — идут одной и той же рукой, но правки уходят
   * в разные места. Держим их порознь: одновременно включить оба нельзя, иначе человек
   * не знал бы, двигает он экран себе или всей фирме.
   */
  const sharing = arranging && state.mode === 'shared';
  const personalizing = arranging && state.mode === 'personal';
  /**
   * Витрина Типов Блоков на экране прямо сейчас. Кнопка «Добавить Блок» переключает именно
   * её, а не память о ней: намерение «открыть» рождается от увиденного, и если витрины не
   * видно, нажатие обязано открывать, а не закрывать.
   */
  const showcase = adding && sharing;

  const switchTo = (mode: 'shared' | 'personal'): void => {
    const on = !(arranging && state.mode === mode);
    // Выключая личный режим, возвращаем ядро к общему: следующая правка Администратора
    // иначе ушла бы ему же в личные места, и общая Раскладка не сдвинулась бы.
    editor.setMode(on ? mode : 'shared');
    setArranging(on);
    setGrab(null);
    setGhost(null);
    // Витрина закрывается вместе со сборкой. Оставь мы её «открытой» в памяти — на экране
    // её всё равно нет (она рисуется только при общей сборке), а следующее нажатие
    // «Добавить Блок» уходило бы на закрытие несуществующего, и кнопка молчала бы через раз.
    setAdding(false);
    // Обводка добавленного живёт ровно столько, сколько идёт сборка: закончил — экран
    // снова обычный, без следов последнего действия.
    setFresh(null);
  };

  /**
   * Открыть или закрыть выбор Блока. Общую сборку включаем заодно: человек, нажавший
   * «добавить», уже сказал, чего хочет, и требовать от него сперва отдельно войти в режим —
   * значит спрятать действие за паролем, который сам же и выдаёшь.
   */
  const openAdd = (): void => {
    const next = !showcase;
    setAdding(next);
    if (!next || sharing) return;
    editor.setMode('shared');
    setArranging(true);
    setGrab(null);
    setGhost(null);
  };

  /**
   * Правка личного вида из меню Блока: свернуть, скрыть, закрепить, покрасить. Про скрытие
   * говорим голосом отдельно — Блок исчезает с экрана, и без слов это читается как поломка,
   * а не как своё же действие.
   */
  const changeView = useCallback(
    async (block: Block, patch: Partial<Block['view']>): Promise<void> => {
      await editor.setView(block.id, patch);
      // Голосом объявляем только то, что вправду случилось. Ядро отказ не выбрасывает, а
      // откатывает вид и кладёт объяснение в состояние; скажи мы «Блок скрыт» вслепую, человек
      // с диктором искал бы Блок в списке скрытых, а тот остался бы на месте.
      if (editor.state().refusal !== null) return;
      if (patch.hidden === true) say(t('block.hiddenSpoken', { title: kindTitle(block) }));
      if (patch.hidden === false) say(t('block.shownSpoken', { title: kindTitle(block) }));
    },
    [editor, kindTitle, say, t],
  );

  /**
   * «Перенести» из меню — тот же захват, что и Enter на заголовке. Захват всегда личный:
   * общая перестановка остаётся отдельной кнопкой «Собрать Раскладку», и случайное нажатие
   * в меню не должно перекраивать экран всей фирме.
   */
  const carry = useCallback(
    (block: Block, mode: 'move' | 'resize' = 'move'): void => {
      if (state.mode !== 'personal') editor.setMode('personal');
      setArranging(true);
      setGrab({ id: block.id, mode, from: null, startPosition: block.position });
      setGhost(block.position);
      say(t(mode === 'resize' ? 'layout.grabbedSize' : 'layout.grabbed'));
      // Фокус — на ручку перетаскивания этого Блока: человек с клавиатуры иначе остался бы
      // с взятым Блоком и фокусом на исчезнувшем пункте меню.
      window.setTimeout(() => {
        const card = gridRef.current?.querySelector(`[data-block="${block.id}"] [role="button"]`);
        (card as HTMLElement | null)?.focus();
      }, 0);
    },
    [editor, say, state.mode, t],
  );

  /**
   * Добавить Блок и показать человеку, что он появился.
   *
   * Новый Блок встаёт в первое свободное место, а оно бывает и ниже края экрана: экран
   * молча оставался прежним, и добавивший читал это как «ничего не добавилось» — с чего
   * правка и началась. Поэтому добавленный Блок называют вслух вместе с его местом, к нему
   * уводят экран и обводят его, пока идёт сборка.
   *
   * Какой Блок родился, узнаём сравнением с тем, что было: сам редактор номера не
   * возвращает — его даёт сервер, и до ответа его не существует.
   */
  const add = useCallback(
    async (kind: BlockKind): Promise<void> => {
      const before = new Set(editor.state().blocks.map((one) => one.id));
      try {
        await editor.add(kind.id);
      } finally {
        // Список закрывается и когда добавить не вышло: рядом уже висит плашка отказа, а
        // открытый список поверх неё читается как «выбери ещё раз» — и человек выбирает.
        setAdding(false);
      }
      const born = editor.state().blocks.find((one) => !before.has(one.id));
      // Не встал — почему, человеку уже сказано плашкой отказа; молчим, чтобы не спорить с ней.
      if (!born) return;
      setFresh(born.id);
      say(
        t('layout.added', {
          title: kind.title,
          col: String(born.position.col + 1),
          row: String(born.position.row + 1),
        }),
      );
    },
    [editor, say, t],
  );

  // Увести экран к добавленному Блоку — после отрисовки, иначе искать ещё нечего. Без
  // плавности: человек просил показать Блок, а не покатать перед ним экран, да и «поменьше
  // движения» из Настроек тут пришлось бы обходить отдельно.
  useEffect(() => {
    if (!fresh) return;
    gridRef.current?.querySelector(`[data-block="${fresh}"]`)?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [fresh]);

  const rows = useMemo(() => {
    const lowest = blocks.reduce(
      (deepest, one) => Math.max(deepest, one.position.row + one.position.height),
      0,
    );
    // Пустой Раскладке всё равно нужна высота — иначе кнопка «добавить» висит над пустотой
    // без единого намёка, куда Блок встанет.
    return Math.max(lowest, 6);
  }, [blocks]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="me-auto text-lg font-semibold">{t('layout.title')}</h2>

        {/* На телефоне вместо кнопки сборки — объяснение: Раскладка здесь показана
            столбиком, двигать в ней нечего, а собирают её с большого экрана. */}
        {canArrange && stacked ? (
          <p className="text-sm text-ink-soft">{t('layout.onlyWide')}</p>
        ) : null}

        {canArrange && !stacked ? (
          <Button
            look={sharing ? 'primary' : 'plain'}
            aria-pressed={sharing}
            disabled={personalizing}
            onClick={() => switchTo('shared')}
          >
            {sharing ? t('layout.editOff') : t('layout.editOn')}
          </Button>
        ) : null}

        {/* Настроить под себя — на том же месте и по тем же правилам, что общая сборка:
            на телефоне сетки нет, поэтому и здесь предлагать нечего. */}
        {canPersonalize && !stacked ? (
          <Button
            look={personalizing ? 'primary' : 'plain'}
            aria-pressed={personalizing}
            disabled={sharing}
            onClick={() => switchTo('personal')}
          >
            {personalizing ? t('layout.personalOff') : t('layout.personalOn')}
          </Button>
        ) : null}

        {/* «Вернуть как у всех» показываем, только когда есть что возвращать: человеку,
            ничего не двигавшему, эта кнопка обещала бы несделанное. */}
        {canPersonalize && state.personal ? (
          <Button
            look="plain"
            disabled={state.saving}
            onClick={() => {
              void editor.resetPersonal();
              setGrab(null);
              setGhost(null);
              say(t('layout.personalReset'));
            }}
          >
            {t('layout.personalForget')}
          </Button>
        ) : null}

        {/* «Добавить Блок» стоит рядом с «собрать Раскладку», а не внутри неё (правка
            2026-08-31). Раньше кнопка появлялась только после включения сборки, и человек,
            искавший, где взять Блоки, не находил их вовсе: спрятанное за режимом действие
            равносильно отсутствующему. Теперь кнопка на виду всегда, а сборку она при
            надобности включает сама — это её дело, не человека. */}
        {canArrange && !stacked ? (
          <Button
            look="plain"
            aria-expanded={showcase}
            disabled={personalizing}
            onClick={openAdd}
          >
            {t('layout.addBlock')}
          </Button>
        ) : null}
      </div>

      {/* Пока идёт личная настройка, человеку прямо сказано, чей это порядок: иначе он
          передвинет Блок и решит, что перекроил экран всей фирме. */}
      {personalizing ? <p className="text-sm text-ink-soft">{t('layout.personalHint')}</p> : null}

      {/* Пока идёт сборка — словами, чем Блок двигают и чем растягивают (правка
          2026-08-31). Ручка перетаскивания и уголок размера видны только тому, кто уже
          знает, что они есть: заголовок выглядит как заголовок, уголок — как край рамки.
          Без этой строки человек включал сборку и не находил, за что взяться. */}
      {sharing ? <p className="text-sm text-ink-soft">{t('layout.arrangeHint')}</p> : null}

      {/* Выбор Блока — плитками, а не строчками с кнопками (правка 2026-08-31). Прежний
          список читался как перечень настроек: название сидело на маленькой кнопке, пояснение
          лежало рядом обычным текстом, и было не видно ни что здесь витрина, ни что нажимать
          надо на само название. Плитка — целиком кнопка: куда бы человек ни попал, он выбрал
          Блок, а не промахнулся мимо кнопки в подпись. */}
      {showcase ? (
        <div className="rounded-md border border-line bg-surface p-4">
          <h3 className="text-base font-semibold text-ink">{t('layout.addChoose')}</h3>
          <p className="mt-1 text-sm text-ink-soft">{t('layout.addHint')}</p>
          {/* Куда именно ляжет Блок — до выбора Типа, а не после: человек с несколькими
              Каналами на виду иначе ищет добавленное не в том Канале. */}
          {where ? <p className="mt-1 text-sm text-ink-soft">{t('layout.addWhere', { where })}</p> : null}
          {/* Витрина названа своим именем: на Раскладке рядом стоят уже добавленные Блоки, и
              их заголовки зовутся так же. Без имени у списка и диктор, и проверка одинаково
              не отличают «Переписка — плитка на витрине» от «Переписка — Блок на экране». */}
          <ul
            aria-label={t('layout.addChoose')}
            className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
          >
            {state.kinds.map((kind) => {
              const ready = READY_BLOCK_KINDS.has(kind.id);
              return (
                <li key={kind.id} className="flex">
                  <button
                    type="button"
                    disabled={state.saving}
                    onClick={() => void add(kind)}
                    className="flex w-full flex-col items-start gap-1 rounded-md border border-line bg-surface-2 p-3 text-start transition-colors hover:border-accent hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span className="flex w-full flex-wrap items-center gap-2">
                      <span className="font-semibold text-ink">{kind.title}</span>
                      {/* Тип, которого оболочка ещё не рисует, честно предупреждает об этом
                          до нажатия: иначе на экране появляется рамка с объяснением вместо
                          обещанного, и это читается как поломка. */}
                      {ready ? null : (
                        <span className="rounded border border-line px-1.5 py-0.5 text-xs text-ink-soft">
                          {t('layout.notReadyTag')}
                        </span>
                      )}
                    </span>
                    {/* Что Блок делает — под названием. Одного названия мало: «Показатели»
                        или «Сданные работы» ничего не обещают, и Блок выбирали вслепую. */}
                    {kind.about ? <span className="text-sm text-ink-soft">{kind.about}</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {/* Скрытые Блоки — списком над Раскладкой. Не «где-то в настройках»: скрыл человек
          одним нажатием, и вернуть должен так же, не вспоминая, куда за этим идти. */}
      {hidden.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm">
          <span className="text-ink-soft">{t('block.hiddenList', { count: String(hidden.length) })}</span>
          {hidden.map((block) => (
            <Button
              key={block.id}
              look="plain"
              disabled={state.saving}
              onClick={() => void changeView(block, { hidden: false })}
            >
              {t('block.show', { title: kindTitle(block) })}
            </Button>
          ))}
        </div>
      ) : null}

      {state.refusal ? (
        <Notice kind="error" onClose={() => editor.dismissRefusal()} closeLabel={t('common.close')}>
          {state.refusal}
        </Notice>
      ) : null}

      {askRemove ? (
        <Notice kind="info">
          <div className="flex flex-wrap items-center gap-3">
            <span>{t('layout.removeConfirm')}</span>
            <Button
              look="danger"
              onClick={() => {
                // Удаление — правка общая, и ядро в личном режиме её не примет. Возвращаем
                // режим сами: отказ «под себя настраивается только расположение» в ответ на
                // «да, убрать» человек прочитал бы как поломку, а не как правило.
                editor.setMode('shared');
                void editor.remove(askRemove);
                setAskRemove(null);
              }}
            >
              {t('common.yes')}
            </Button>
            <Button onClick={() => setAskRemove(null)}>{t('common.no')}</Button>
          </div>
        </Notice>
      ) : null}

      {/* Голос для тех, кто не смотрит на экран: движение Блока иначе никак не заметно. */}
      <p aria-live="polite" className="ktm-only-voice">
        {spoken}
      </p>

      {state.loading ? <p className="text-ink-soft">{t('common.loading')}</p> : null}

      {!state.loading && blocks.length === 0 ? (
        <p className="text-ink-soft">
          {t('layout.empty')}
          {canArrange ? ` ${t('layout.emptyAdmin')}` : ''}
        </p>
      ) : null}

      <div
        ref={gridRef}
        // Направление сетки своё и всегда одно (G12) — см. заголовок файла.
        dir={layoutDirectionOf()}
        className={stacked ? 'flex min-h-0 flex-1 flex-col gap-3' : 'grid min-h-0 flex-1 gap-3'}
        style={
          stacked
            ? undefined
            : {
                gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
                gridAutoRows: `${ROW_HEIGHT_REM}rem`,
                gridTemplateRows: `repeat(${rows}, ${ROW_HEIGHT_REM}rem)`,
              }
        }
      >
        {blocks.map((block) => (
          <BlockCard
            key={block.id}
            blockId={block.id}
            title={kindTitle(block)}
            position={shown(block)}
            stacked={stacked}
            arranging={arranging && !stacked}
            grabbed={grab?.id === block.id}
            fresh={fresh === block.id}
            freshLabel={t('layout.addedHere')}
            onGrabPointer={startPointer(block, 'move')}
            onResizePointer={startPointer(block, 'resize')}
            onKeyDown={onKeyDown(block)}
            view={block.view}
            busy={state.saving}
            // Свернуть и покрасить может каждый, кому виден Канал, — это его экран. Гостю
            // личного вида не полагается, и меню у него без этих пунктов вовсе.
            {...(canPersonalize ? { onView: (patch) => void changeView(block, patch) } : {})}
            // «Перенести» — только там, где есть куда нести: на телефоне сетки нет, а
            // закреплённый Блок сперва открепляют тем же меню.
            {...(canPersonalize && !stacked && !block.view.pinned
              ? { onMove: () => carry(block), onResize: () => carry(block, 'resize') }
              : {})}
            // Убрать Блок можно только из общей Раскладки: состав Канала — дело
            // Администратора, и «под себя» его не меняют.
            {...(canArrange ? { onRemove: () => setAskRemove(block.id) } : {})}
          >
            {renderBlock(block)}
          </BlockCard>
        ))}
      </div>
    </div>
  );
}
